import { httpApi } from "api/http.api";
import { IAffilicatioResponse, IFilterData, IOrderData, IOrderData2, IOrderGarmentPayload, IPrintResponse, IReadyResponse, PayloadInterface } from "domain/order";

export const getOrderOverview = (): Promise<any[]> =>
  httpApi.get<any[]>("OrderView/Clients").then(({ data }) => data);

export const getOrderByCompany = (): Promise<any[]> =>
  httpApi.get<any[]>("OrderView/Garments").then(({ data }) => data);

export const getOpenOrderProfessionals = (data: any = null): Promise<any[]> => {
  return httpApi.get<any[]>(`OrderView/OpenOrderProfessionals/${data}`).then((data: any) => data.data.data);
}

export const getOpenGarmentOrderByProfessional = (data: IOrderData2): Promise<any[]> => {
  const {
    current, pageSize,
    ...rest
  } = data;
  let apiUrl = "OrderViewV2/OpenOrderGarments"

  if (data) {
    const filterQueryString = buildFilterQueryTwoStringFormat(rest);
    let queryParameters = [`$count=true`];

    if (typeof pageSize !== 'undefined') {
      if (current === 1) {
        queryParameters.push(`$top=${pageSize}`);
      } else {
        const skipValue = (current - 1) * pageSize;
        if (!isNaN(skipValue)) {
          queryParameters.push(`$top=${pageSize}&$skip=${skipValue}`);
        }
      }
    }

    if (filterQueryString) {
      queryParameters.push(filterQueryString);
    }

    apiUrl += `?${queryParameters.join('&')}`;

    return httpApi.get<any[]>(apiUrl)
      .then(({ data }) => data);
  }
  return Promise.resolve([]);
}


export const getOrderByProfessional = (data: any): Promise<any[]> => {
  return httpApi.get<any[]>(`OrderView/GarmentsOfProfessional2/${data.client_id}/${data.professional_id}`).then(({ data }) => data);
}

export const getAffiliationsOfGarment = (data: any): Promise<any[]> => {
  return httpApi.get<any[]>(`/OrderView/AffiliationsOfGarment2/${data.client_id}/${data.professional_id}/${data.garment_id}/${data.order_id}`).then(({ data }) => data);
}

export const getAffiliationsOrderOfGarmentItems = (data: IOrderData): Promise<any[]> => {
  const {
    top,
    skip,
    pagination,
    current, pageSize,
    ...rest
  } = data;

  let apiUrl = "/OpenOrderItemsViewV2/OpenOrderItemsByGarment";


  if (data) {
    const filterQueryString = buildFilterQueryTwoStringFormat(rest);
    let queryParameters = [`$count=true`];

    if (filterQueryString) {
      queryParameters.push(filterQueryString);
    }

    apiUrl += `?${queryParameters.join('&')}`;

    return httpApi.get<any[]>(apiUrl)
      .then(({ data }) => data);
  }

  return Promise.resolve([]);
};

export const getCompanyOrderData = (clientIds: any): Promise<any[]> => {
  return httpApi.get<any[]>(`OrderView/Garments/${clientIds}`).then((data: any) => data?.data?.data);
}

export const postReadyForAffiliation = (data: any): Promise<any[]> => {
  return httpApi.post<any[]>(`Affiliations/ChangeAffiliationOrderStatusToReady`, data).then(({ data }) => data);
}

export const postReadyForAffiliationV2 = (data: any): Promise<IReadyResponse> => {
  return httpApi.post<IReadyResponse>(`Affiliations/ChangeAffiliationOrderStatusToReadyV2`, data).then(({ data }) => data);
}


export const printAfflication = (data: any): Promise<IPrintResponse> => {
  return httpApi.post<IPrintResponse>(`OrderView/ExportBarcodes2/`, data).then(({ data }) => data);
}

export const buildFilterQueryTwoStringFormat = (data: any) => {
  const filters = Object.entries(data)
    .filter(([key, value]) => value !== null && value !== undefined && !key.endsWith("_isComplete"))
    .map(([key, value]: any) => {
      if (key === "from_date" || key === "to_date") {
        const dateObj = new Date(value);
        value = isNaN(dateObj.getTime()) ? null : dateObj.toISOString();
      }

      const conditionalKeys = new Set([
        "professional_name",
        "item_name",
        "garment_size",
        "color",
        "fabric",
        "amount",
        "barcode",
      ]);

      if (conditionalKeys.has(key)) {
        const isComplete = data[`${key}_isComplete`] === true;

        return isComplete
          ? `${key} eq '${encodeURIComponent(value)}'`
          : `contains(${key}, '${value}')`;
      }      
      return `${key} eq ${encodeURIComponent(value)}`;
    });

  if (filters.length > 0) {
    return "$filter=" + filters.join(" and ");
  } else {
    return "";
  }
};

export const buildProfessionalFilterQueryTwoStringFormat = (data: any) => {
  const stringFields = [
    "professional_name",
    "item_name",
    "company_name",
    "order_type",
    "color",
    "garment_size",
    "amount",
    "barcode",
    "comments",
  ];
  const exactMatchFields = ["client_id"];
  const numericFields = ["sequence_number", "amount"];
  const dateFields = ["date_opened"];
  const filters = Object.entries(data)
    .filter(([key, value]) => value !== null && value !== undefined && !key.endsWith("_isComplete"))
    .map(([key, value]: [string, any]) => {
      if (key === "from_date" || key === "to_date") {
        return `${key} eq ${value.toISOString()}`;
      }
      if (dateFields.includes(key)) {
        return `${key} eq ${value}`;
      }
      if (numericFields.includes(key)) {
        return `${key} eq ${value}`;
      }
      if (key === "professional_name") {
        const isComplete = data["professional_name_isComplete"] === true;
        const encodedValue = value.replace(/'/g, "''");
        return isComplete ? `${key} eq '${encodedValue}'` : `contains(${key}, '${encodedValue}')`;
      }
      if (stringFields.includes(key) && typeof value === "string") {
        const encodedValue = value.replace(/'/g, "''");
        return `contains(${key}, '${encodedValue}')`;
      }
      if (exactMatchFields.includes(key) || typeof value === "number") {
        return `${key} eq ${value}`;
      }
      const encodedValue = value.toString().replace(/'/g, "''");
      return `${key} eq '${encodedValue}'`;
    });

  if (filters.length > 0) {
    return "$filter=" + filters.join(" and ");
  } else {
    return "";
  }
};

export const updateOrderGarments = (
  payload: any
): Promise<IAffilicatioResponse> => {
  const { affiliation_id, comments } = payload;
  return httpApi.put(`Affiliations/UpdateAffiliationComment/${affiliation_id}`,
    {
      comments
    }).then(({ data }) => data);
};

export const getAllProfessionalData = (): Promise<any> =>
  httpApi
    .get<{ value: any[] }>("OpenOrderItemsViewV2/OpenOrderItemsByGarment")
    .then(({ data }) => {
      return data.value;
    });

export const getFilterProfessionalData = (data: any): Promise<IFilterData> => {
  const {
    top = 10,
    skip = 0,
    pagination,
    current, pageSize,
    ...rest
  } = data;
  let apiUrl = "/OpenOrderItemsViewV2/OpenOrderItemsByGarment";
  if (data) {
    const filterQueryString = buildProfessionalFilterQueryTwoStringFormat(rest);
    let queryParameters = [`$count=true`, `$top=${top}`, `$skip=${skip}`];
    if (filterQueryString) {
      queryParameters.push(filterQueryString);
    }
    apiUrl += `?${queryParameters.join('&')}`;
    return httpApi.get<IFilterData>(apiUrl).then(({ data }) => data);
  }
    return Promise.resolve({
    '@odata.context': '',
    '@odata.count': 0,
    value: []
  });
};
